import {memo, useMemo} from "react";
import {useIsMinDesktopSizeL, useIsMinTabletSize} from "../common/hooks/useMediaQuery";
import {type ColumnDef} from "@tanstack/react-table";
import {TanStackTable} from "~/common/components/Table/TanStackTable";
import {useTranslation} from "react-i18next";
import {getTariffNumbers} from "~/utils/productUtils";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

const data = [{ index: 0 }, { index: 1 }, { index: 2 }];

interface CommercialTableProps {
  products: ProductDescriptor[];
  placeholders: number[];
  colSize: number;
}
export interface RowData {
  index: number;
}

const CommercialTable = memo(({ products, placeholders, colSize }: CommercialTableProps) => {
  const { t } = useTranslation();
  const isDesktopL = useIsMinDesktopSizeL();
  const isTablet = useIsMinTabletSize();

  const tableProducts = products.map((product: ProductDescriptor) => {
    const tariffNumber = getTariffNumbers(product.base).join(", ");
    const country = product.prices.map((price) => price.originCountry).join(", ");
    const gpsdGroup = product?.base?.gpsdGroup;

    return {
      tariffNumber,
      country,
      gpsdGroup,
    };
  });

  const columnConfig: ColumnDef<RowData>[] = useMemo(() => {
    const columnConfig: ColumnDef<RowData>[] = [];

    const labelCol: ColumnDef<RowData> = {
      id: "label",
      header: t("commercialTable.tableName") as string,
      size: 224,

      cell: (cell) => {
        const data = cell.row.original;
        switch (data.index) {
          case 0:
            return t("commercialTable.tariffNumber");
          case 1:
            return t("common.product.countryOfOrigin");
          case 2:
            return t("common.product.gpsdGroup");
          default:
            return null;
        }
      },
    };

    const makeProductCol = (index: number) => {
      const productCol: ColumnDef<RowData> = {
        id: `product-${index}`,
        header: "",
        size: colSize,
        cell: (cell) => {
          const data = cell.row.original;
          const product = tableProducts[index];
          switch (data.index) {
            case 0:
              return product?.tariffNumber ?? null;
            case 1:
              return product?.country ?? null;
            case 2:
              return product?.gpsdGroup ?? null;
            default:
              return null;
          }
        },
      };
      return productCol;
    };

    isTablet && columnConfig.push(labelCol);
    products.forEach((product, index) => {
      columnConfig.push(makeProductCol(index));
    });
    placeholders.forEach((product) => {
      columnConfig.push(makeProductCol(product));
    });

    if (isDesktopL) {
      const len = columnConfig.length;
      columnConfig[len - 1].size = undefined;
    }

    return columnConfig;
  }, [tableProducts, isDesktopL]);

  return (
    <TanStackTable
      data={data}
      columns={columnConfig}
      variant={"productComparison"}
      state={{ columnPinning: { left: ["label"] } }}
    />
  );
});

export default CommercialTable;
